import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";
import { HFButton } from "@/components/ui-hf";
import {
    Download, LayoutGrid, Rows, ArrowLeft, Trash2, RefreshCw,
} from "lucide-react";
import {
    DndContext, DragEndEvent, DragOverlay, DragStartEvent,
    PointerSensor, useSensor, useSensors,
    useDraggable, useDroppable,
} from "@dnd-kit/core";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

const STATUSES = [
    { key: "neu", label: "Neu", tone: "highlight" },
    { key: "kontaktiert", label: "Kontaktiert", tone: "neutral" },
    { key: "angeboten", label: "Angeboten", tone: "neutral" },
    { key: "auftrag", label: "Auftrag", tone: "success" },
    { key: "erledigt", label: "Erledigt", tone: "muted" },
];

type ContactRequest = {
    id: string; created_at: string; status: string; name: string; email: string;
    firma: string; phone: string; object_type: string; location: string;
    package: string; grundstueck: string; wunschtermin: string; message: string;
    files: Array<{ original: string; stored: string; size: number }>;
    note?: string; ip?: string;
    vt_code?: string | null;
    vt_number?: number | null;
    vt_name?: string | null;
    source?: string | null;
    activity_log?: Array<{ at: string; type: string; by: string; value?: string }>;
    is_demo?: boolean;
};

function statusChipStyle(status: string): React.CSSProperties {
    if (status === "neu") return { background: "hsl(var(--hf-gold-soft) / 0.7)", color: "hsl(var(--hf-ink))" };
    if (status === "kontaktiert") return { background: "hsl(210 30% 88%)", color: "hsl(var(--hf-ink))" };
    if (status === "angeboten") return { background: "hsl(35 60% 80%)", color: "hsl(var(--hf-ink))" };
    if (status === "auftrag") return { background: "hsl(140 40% 82%)", color: "hsl(var(--hf-ink))" };
    if (status === "erledigt") return { background: "hsl(var(--hf-surface-2))", color: "hsl(var(--hf-muted))" };
    return { background: "hsl(var(--hf-surface))", color: "hsl(var(--hf-ink))" };
}

// ---------- List (Table + Kanban toggle) ----------
export function AdminAnfragenListPage() {
    const [items, setItems] = useState<ContactRequest[]>([]);
    const [error, setError] = useState("");
    const [view, setView] = useState<"table" | "kanban">("table");
    const [filter, setFilter] = useState<string>("");
    const [sourceFilter, setSourceFilter] = useState<"" | "referenzaktion" | "aktion" | "gutschein">("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await api<{ items: ContactRequest[] }>("/api/admin/contact-requests");
        if (res.ok && res.data) setItems(res.data.items);
        else setError(res.error || "Konnte Anfragen nicht laden.");
        setLoading(false);
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const filtered = useMemo(
        () => {
            let out = items;
            if (filter) out = out.filter((i) => i.status === filter);
            if (sourceFilter) out = out.filter((i) => (i.source || "") === sourceFilter);
            return out;
        },
        [items, filter, sourceFilter],
    );

    const grouped = useMemo(() => {
        const g: Record<string, ContactRequest[]> = {};
        STATUSES.forEach((s) => (g[s.key] = []));
        items.forEach((it) => {
            (g[it.status] ||= []).push(it);
        });
        return g;
    }, [items]);

    const setStatus = async (id: string, status: string) => {
        const res = await api<ContactRequest>(`/api/admin/contact-requests/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status }),
        });
        if (res.ok && res.data) {
            setItems((prev) => prev.map((p) => (p.id === id ? res.data! : p)));
        } else {
            setError(res.error || "Status konnte nicht aktualisiert werden.");
        }
    };

    return (
        <div data-testid="admin-anfragen">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div className="hf-eyebrow">Kontakt · Inbox</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">Anfragen</h1>
                    <p className="mt-1 text-hf-muted">
                        Alle Anfragen aus dem Kontaktformular. Status per Klick ändern.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div
                        className="inline-flex rounded-sm border overflow-hidden"
                        style={{ borderColor: "hsl(var(--hf-line))" }}
                    >
                        <button
                            type="button"
                            onClick={() => setView("table")}
                            className="px-3 py-2 text-xs inline-flex items-center gap-1.5"
                            data-testid="anfragen-view-table"
                            style={{
                                background: view === "table" ? "hsl(var(--hf-ink))" : "hsl(var(--hf-bg))",
                                color: view === "table" ? "hsl(var(--hf-bg))" : "hsl(var(--hf-ink))",
                            }}
                        >
                            <Rows size={13} /> Tabelle
                        </button>
                        <button
                            type="button"
                            onClick={() => setView("kanban")}
                            className="px-3 py-2 text-xs inline-flex items-center gap-1.5"
                            data-testid="anfragen-view-kanban"
                            style={{
                                background: view === "kanban" ? "hsl(var(--hf-ink))" : "hsl(var(--hf-bg))",
                                color: view === "kanban" ? "hsl(var(--hf-bg))" : "hsl(var(--hf-ink))",
                            }}
                        >
                            <LayoutGrid size={13} /> Kanban
                        </button>
                    </div>
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="px-3 py-2 text-xs rounded-sm bg-white border"
                        style={{ borderColor: "hsl(var(--hf-line))" }}
                        data-testid="anfragen-filter-status"
                    >
                        <option value="">Alle Status</option>
                        {STATUSES.map((s) => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                    </select>
                    <select
                        value={sourceFilter}
                        onChange={(e) => setSourceFilter(e.target.value as typeof sourceFilter)}
                        className="px-3 py-2 text-xs uppercase tracking-wider rounded-sm bg-white border"
                        style={{ borderColor: "hsl(var(--hf-line))", color: "hsl(var(--hf-ink))" }}
                        title="Nach Quelle filtern"
                        data-testid="anfragen-filter-source"
                    >
                        <option value="">Alle Quellen</option>
                        <option value="referenzaktion">Referenzaktion</option>
                        <option value="aktion">Aktions-Landing</option>
                        <option value="gutschein">Gutschein-Popup</option>
                    </select>
                    <button
                        type="button"
                        onClick={() => void load()}
                        className="hf-btn hf-btn-ghost"
                        data-testid="anfragen-refresh"
                    >
                        <RefreshCw size={13} /> <span>Aktualisieren</span>
                    </button>
                    <a
                        href={`${BACKEND_URL}/api/admin/contact-requests/export.csv`}
                        className="hf-btn hf-btn-secondary"
                        data-testid="anfragen-export-csv"
                    >
                        <Download size={13} /> <span>CSV</span>
                    </a>
                </div>
            </div>

            {error && (
                <div
                    className="mt-6 p-3 rounded text-sm"
                    style={{
                        background: "hsl(var(--hf-danger) / 0.12)",
                        color: "hsl(var(--hf-danger))",
                        border: "1px solid hsl(var(--hf-danger) / 0.3)",
                    }}
                >
                    {error}
                </div>
            )}

            {loading ? (
                <div className="mt-10 text-hf-muted text-sm">Wird geladen…</div>
            ) : view === "table" ? (
                <div
                    className="mt-8 rounded-sm overflow-hidden"
                    style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}
                    data-testid="anfragen-table"
                >
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr
                                    className="text-left uppercase tracking-[0.14em] text-[10px] text-hf-muted"
                                    style={{ background: "hsl(var(--hf-surface))" }}
                                >
                                    <th className="px-4 py-3">Datum</th>
                                    <th className="px-4 py-3">Name</th>
                                    <th className="px-4 py-3">E-Mail</th>
                                    <th className="px-4 py-3">Objekt</th>
                                    <th className="px-4 py-3">Paket</th>
                                    <th className="px-4 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-8 text-center text-hf-muted">
                                            Keine Anfragen unter diesem Filter.
                                        </td>
                                    </tr>
                                )}
                                {filtered.map((c) => (
                                    <tr
                                        key={c.id}
                                        data-testid={`anfragen-row-${c.id}`}
                                        className="border-t hover:bg-hf-surface transition-colors"
                                        style={{ borderColor: "hsl(var(--hf-line))" }}
                                    >
                                        <td className="px-4 py-3 text-hf-muted whitespace-nowrap">
                                            {fmtDateTime(c.created_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Link
                                                    to={`/admin/anfragen/${c.id}`}
                                                    className="text-hf-ink hover:text-hf-gold-dark font-medium"
                                                    data-testid={`anfragen-link-${c.id}`}
                                                >
                                                    {c.name}
                                                </Link>
                                                {c.is_demo && (
                                                    <span
                                                        className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                                                        style={{ background: "hsl(45 90% 60%)", color: "hsl(var(--hf-ink))" }}
                                                        data-testid={`anfragen-demo-badge-${c.id}`}
                                                    >
                                                        Demo
                                                    </span>
                                                )}
                                                {c.vt_code && (
                                                    <span
                                                        className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                                                        style={{ background: "hsl(var(--hf-gold) / 0.25)", color: "hsl(var(--hf-ink))" }}
                                                        title={`via Vertreter: ${c.vt_code}`}
                                                        data-testid={`anfragen-vt-badge-${c.id}`}
                                                    >
                                                        {c.vt_number != null
                                                            ? `via Nr. ${c.vt_number}${c.vt_name ? ` · ${c.vt_name}` : ""}`
                                                            : `via ${c.vt_code}`}
                                                    </span>
                                                )}
                                                {c.source === "referenzaktion" && (
                                                    <span
                                                        className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                                                        style={{ background: "#A78150", color: "#FFFDF9", fontWeight: 600 }}
                                                        title="Anfrage aus der Referenz-Aktion"
                                                        data-testid={`anfragen-aktion-badge-${c.id}`}
                                                    >
                                                        Referenz
                                                    </span>
                                                )}
                                                {c.source === "aktion" && (
                                                    <span
                                                        className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                                                        style={{ background: "#0F1830", color: "#FFFDF9", fontWeight: 600 }}
                                                        title="Anfrage aus der Aktions-Landing"
                                                        data-testid={`anfragen-source-aktion-${c.id}`}
                                                    >
                                                        Aktion
                                                    </span>
                                                )}
                                                {c.source === "gutschein" && (
                                                    <span
                                                        className="text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 rounded-sm"
                                                        style={{ background: "#DC2626", color: "#FFFDF9", fontWeight: 700 }}
                                                        title="Reservierung aus dem Gutschein-Popup"
                                                        data-testid={`anfragen-source-gutschein-${c.id}`}
                                                    >
                                                        Gutschein
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-hf-muted">{c.firma}</div>
                                        </td>
                                        <td className="px-4 py-3 text-hf-muted">{c.email}</td>
                                        <td className="px-4 py-3">
                                            <div>{c.object_type || "—"}</div>
                                            <div className="text-xs text-hf-muted">{c.location}</div>
                                        </td>
                                        <td className="px-4 py-3 text-hf-muted">{c.package || "—"}</td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={c.status}
                                                onChange={(e) => void setStatus(c.id, e.target.value)}
                                                className="px-2 py-1 rounded-sm text-xs"
                                                style={statusChipStyle(c.status)}
                                                data-testid={`anfragen-status-${c.id}`}
                                            >
                                                {STATUSES.map((s) => (
                                                    <option key={s.key} value={s.key}>
                                                        {s.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <KanbanBoard
                    grouped={grouped}
                    onMove={(id, status) => void setStatus(id, status)}
                />
            )}
        </div>
    );
}

// ---------- Kanban DnD ----------
function DraggableCard({ c }: { c: ContactRequest }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: c.id });
    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            data-testid={`anfragen-card-${c.id}`}
            className="block p-3 rounded-sm cursor-grab active:cursor-grabbing select-none"
            style={{
                background: "hsl(var(--hf-surface) / 0.6)",
                border: "1px solid hsl(var(--hf-line))",
                opacity: isDragging ? 0.4 : 1,
            }}
        >
            <div className="text-sm font-medium text-hf-ink truncate flex items-center gap-2">
                {c.name}
                {c.is_demo && (
                    <span
                        className="text-[8px] tracking-[0.14em] uppercase px-1 py-0.5 rounded-sm"
                        style={{ background: "hsl(45 90% 60%)", color: "hsl(var(--hf-ink))" }}
                        data-testid={`anfragen-demo-badge-${c.id}`}
                    >
                        Demo
                    </span>
                )}
            </div>
            <div className="text-xs text-hf-muted truncate">
                {c.object_type || "—"} · {c.location || "—"}
            </div>
            <div className="text-[10px] text-hf-muted mt-1 flex items-center justify-between">
                <span>{fmtDateTime(c.created_at, { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
                <Link
                    to={`/admin/anfragen/${c.id}`}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="underline text-hf-ink hover:text-hf-gold-dark"
                    data-testid={`anfragen-card-open-${c.id}`}
                >
                    öffnen
                </Link>
            </div>
        </div>
    );
}

function DroppableColumn({
    statusKey, label, count, children,
}: { statusKey: string; label: string; count: number; children: React.ReactNode }) {
    const { setNodeRef, isOver } = useDroppable({ id: `col-${statusKey}` });
    return (
        <div
            ref={setNodeRef}
            data-testid={`anfragen-kanban-col-${statusKey}`}
            className="p-3 rounded-sm min-h-[280px] transition-colors"
            style={{
                background: isOver ? "hsl(var(--hf-gold-soft) / 0.35)" : "hsl(var(--hf-bg))",
                border: isOver ? "1px dashed hsl(var(--hf-gold-dark))" : "1px solid hsl(var(--hf-line))",
            }}
        >
            <div className="flex items-center justify-between mb-3">
                <span className="hf-eyebrow">{label}</span>
                <span
                    className="text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-sm"
                    style={statusChipStyle(statusKey)}
                >
                    {count}
                </span>
            </div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function KanbanBoard({
    grouped, onMove,
}: {
    grouped: Record<string, ContactRequest[]>;
    onMove: (id: string, status: string) => void;
}) {
    const [dragId, setDragId] = useState<string | null>(null);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    );

    const findCard = (id: string): ContactRequest | undefined =>
        Object.values(grouped).flat().find((c) => c.id === id);

    const onDragStart = (e: DragStartEvent) => setDragId(String(e.active.id));
    const onDragEnd = (e: DragEndEvent) => {
        setDragId(null);
        const over = e.over?.id ? String(e.over.id) : null;
        const active = String(e.active.id);
        if (!over || !over.startsWith("col-")) return;
        const target = over.slice(4);
        const src = findCard(active);
        if (!src || src.status === target) return;
        onMove(active, target);
    };

    const active = dragId ? findCard(dragId) : null;

    return (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div
                className="mt-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4"
                data-testid="anfragen-kanban"
            >
                {STATUSES.map((s) => {
                    const cards = grouped[s.key] || [];
                    return (
                        <DroppableColumn
                            key={s.key}
                            statusKey={s.key}
                            label={s.label}
                            count={cards.length}
                        >
                            {cards.map((c) => (
                                <DraggableCard key={c.id} c={c} />
                            ))}
                            {cards.length === 0 && (
                                <div className="text-xs text-hf-muted italic px-1 py-4 text-center">
                                    hier ablegen
                                </div>
                            )}
                        </DroppableColumn>
                    );
                })}
            </div>
            <DragOverlay>
                {active && (
                    <div
                        className="p-3 rounded-sm shadow-lg"
                        style={{
                            background: "hsl(var(--hf-bg))",
                            border: "1px solid hsl(var(--hf-gold))",
                            minWidth: 200,
                        }}
                    >
                        <div className="text-sm font-medium text-hf-ink truncate">{active.name}</div>
                        <div className="text-xs text-hf-muted truncate">
                            {active.object_type || "—"} · {active.location || "—"}
                        </div>
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    );
}


// ---------- Detail ----------
export function AdminAnfragenDetailPage() {
    const { id } = useParams<{ id: string }>();
    const nav = useNavigate();
    const [item, setItem] = useState<ContactRequest | null>(null);
    const [error, setError] = useState("");
    const [note, setNote] = useState("");
    const [savedTick, setSavedTick] = useState(false);

    useEffect(() => {
        if (!id) return;
        void (async () => {
            const res = await api<ContactRequest>(`/api/admin/contact-requests/${id}`);
            if (res.ok && res.data) {
                setItem(res.data);
                setNote(res.data.note || "");
            } else {
                setError(res.error || "Anfrage nicht gefunden.");
            }
        })();
    }, [id]);

    // Debounced autosave for note
    useEffect(() => {
        if (!item) return;
        if (note === (item.note || "")) return;
        const t = window.setTimeout(async () => {
            const res = await api<ContactRequest>(`/api/admin/contact-requests/${item.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ note }),
            });
            if (res.ok && res.data) {
                setItem(res.data);
                setSavedTick(true);
                window.setTimeout(() => setSavedTick(false), 1500);
            }
        }, 800);
        return () => window.clearTimeout(t);
    }, [note, item]);

    const changeStatus = async (status: string) => {
        if (!item) return;
        const res = await api<ContactRequest>(`/api/admin/contact-requests/${item.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status }),
        });
        if (res.ok && res.data) setItem(res.data);
    };

    const remove = async () => {
        if (!item) return;
        if (!window.confirm("Diese Anfrage wirklich löschen?")) return;
        const res = await api(`/api/admin/contact-requests/${item.id}`, { method: "DELETE" });
        if (res.ok) nav("/admin/anfragen");
    };

    if (error) return <div className="p-6 text-hf-danger">{error}</div>;
    if (!item) return <div className="p-6 text-hf-muted">Wird geladen…</div>;

    return (
        <div data-testid="admin-anfrage-detail" className="max-w-4xl">
            <Link
                to="/admin/anfragen"
                className="inline-flex items-center gap-1 text-sm text-hf-muted hover:text-hf-ink transition-colors"
            >
                <ArrowLeft size={14} /> Zurück zur Liste
            </Link>
            <div className="mt-3 flex flex-wrap items-baseline justify-between gap-3">
                <div>
                    <div className="hf-eyebrow">Anfrage · {fmtDateTime(item.created_at)}</div>
                    <h1 className="font-display text-3xl md:text-4xl mt-2">{item.name}</h1>
                    <div className="mt-1 text-hf-muted">{item.email} · {item.phone || "—"}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        value={item.status}
                        onChange={(e) => void changeStatus(e.target.value)}
                        className="px-3 py-2 rounded-sm text-xs"
                        style={statusChipStyle(item.status)}
                        data-testid="anfrage-detail-status"
                    >
                        {STATUSES.map((s) => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={() => void remove()}
                        className="hf-btn hf-btn-ghost"
                        data-testid="anfrage-detail-delete"
                    >
                        <Trash2 size={13} /> <span>Löschen</span>
                    </button>
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                    <div className="hf-eyebrow">Nachricht</div>
                    <div
                        className="mt-2 p-5 rounded-sm text-hf-ink whitespace-pre-wrap"
                        style={{
                            background: "hsl(var(--hf-bg))",
                            border: "1px solid hsl(var(--hf-line))",
                        }}
                    >
                        {item.message}
                    </div>

                    <div className="hf-eyebrow mt-8">Interne Notiz</div>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={5}
                        placeholder="Deine Notizen zu dieser Anfrage (autosave)…"
                        className="mt-2 w-full px-4 py-3 rounded-sm bg-white border text-sm text-hf-ink focus:outline-none focus:ring-2 focus:ring-[hsl(var(--hf-gold))]"
                        style={{ borderColor: "hsl(var(--hf-line))" }}
                        data-testid="anfrage-detail-note"
                    />
                    <div className="mt-1 text-xs text-hf-muted h-5">
                        {savedTick && "Gespeichert."}
                    </div>

                    {item.files.length > 0 && (
                        <>
                            <div className="hf-eyebrow mt-8">Anhänge</div>
                            <ul className="mt-2 space-y-2">
                                {item.files.map((f) => (
                                    <li key={f.stored} className="text-sm text-hf-ink">
                                        {f.original}{" "}
                                        <span className="text-hf-muted">
                                            ({(f.size / 1024).toFixed(1)} KB)
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>

                <aside>
                    <div className="hf-eyebrow">Details</div>
                    <dl className="mt-2 space-y-2 text-sm">
                        <div><dt className="text-hf-muted">Firma</dt><dd>{item.firma || "—"}</dd></div>
                        <div><dt className="text-hf-muted">Objektart</dt><dd>{item.object_type || "—"}</dd></div>
                        <div><dt className="text-hf-muted">Ort</dt><dd>{item.location || "—"}</dd></div>
                        <div><dt className="text-hf-muted">Paket</dt><dd>{item.package || "—"}</dd></div>
                        <div><dt className="text-hf-muted">Grundstück</dt><dd>{item.grundstueck || "—"}</dd></div>
                        <div><dt className="text-hf-muted">Wunschtermin</dt><dd>{item.wunschtermin || "—"}</dd></div>
                        <div><dt className="text-hf-muted">IP</dt><dd className="font-mono text-xs">{item.ip}</dd></div>
                    </dl>

                    <div className="hf-eyebrow mt-8">Aktivität</div>
                    <ul className="mt-2 space-y-2 text-xs text-hf-muted">
                        {(item.activity_log || []).map((a, i) => (
                            <li key={i}>
                                <span className="text-hf-ink">{a.type}</span>
                                {a.value ? ` → ${a.value}` : ""}<br />
                                {fmtDateTime(a.at)} · {a.by}
                            </li>
                        ))}
                    </ul>
                </aside>
            </div>
        </div>
    );
}
